import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuroraBG } from '../components/ui/AuroraBG';
import { Button } from '../components/ui/Button';
import { GlassPanel } from '../components/ui/GlassPanel';
import { PublicNavbar } from '../components/layout/PublicNavbar';
import {
  Coffee,
  Utensils,
  Building2,
  Hotel,
  Package,
  Camera,
  ChefHat,
  Users,
  DollarSign,
  CalendarClock,
  ShoppingCart,
  Stethoscope,
  Dumbbell,
  ArrowRight,
  Check,
  Sparkles,
} from 'lucide-react';

export default function LandingPage() {
  const { t } = useTranslation('landing');

  const scrollToSegment = (id: string) => {
    const element = document.getElementById(id);
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen relative">
      <AuroraBG />
      <PublicNavbar />

      <main className="relative pt-24 pb-16">
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-24">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            {t('hero.title')}
          </h1>
          <p className="text-xl text-white/80 mb-8 max-w-3xl mx-auto">
            {t('hero.subtitle')}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
            <Link to="/register">
              <Button variant="primary" size="lg">
                {t('hero.ctaPrimary')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/pricing">
              <Button variant="secondary" size="lg">
                {t('hero.ctaSecondary')}
              </Button>
            </Link>
          </div>

          <p className="text-sm text-white/60">{t('hero.trustBadge')}</p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-12 max-w-4xl mx-auto">
            {[
              { key: 'coffee', icon: Coffee, id: 'segment-coffee' },
              { key: 'restaurants', icon: Utensils, id: 'segment-restaurants' },
              { key: 'multi', icon: Building2, id: 'segment-multi' },
              { key: 'groups', icon: Hotel, id: 'segment-groups' },
            ].map(({ key, icon: Icon, id }) => (
              <button
                key={key}
                onClick={() => scrollToSegment(id)}
                className="p-4 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-primary-400/50 transition-all group"
              >
                <Icon className="w-6 h-6 text-primary-400 mx-auto mb-2 group-hover:scale-110 transition-transform" />
                <span className="text-sm text-white/90">{t(`hero.segment.${key}`)}</span>
              </button>
            ))}
          </div>
        </section>

        <section id="segment-coffee" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
          <GlassPanel className="p-8">
            <div className="flex items-start gap-4 mb-4">
              <Coffee className="w-10 h-10 text-amber-400 flex-shrink-0" />
              <div>
                <h2 className="text-2xl font-bold text-white mb-4">{t('segments.coffee.title')}</h2>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-success-400 flex-shrink-0 mt-0.5" />
                    <span className="text-white/80">{t('segments.coffee.bullet1')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-success-400 flex-shrink-0 mt-0.5" />
                    <span className="text-white/80">{t('segments.coffee.bullet2')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-success-400 flex-shrink-0 mt-0.5" />
                    <span className="text-white/80">{t('segments.coffee.bullet3')}</span>
                  </li>
                </ul>
                <Button variant="primary">{t('segments.coffee.cta')}</Button>
              </div>
            </div>
          </GlassPanel>
        </section>

        <section id="segment-restaurants" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
          <GlassPanel className="p-8">
            <div className="flex items-start gap-4 mb-4">
              <Utensils className="w-10 h-10 text-rose-400 flex-shrink-0" />
              <div>
                <h2 className="text-2xl font-bold text-white mb-4">{t('segments.restaurants.title')}</h2>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-success-400 flex-shrink-0 mt-0.5" />
                    <span className="text-white/80">{t('segments.restaurants.bullet1')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-success-400 flex-shrink-0 mt-0.5" />
                    <span className="text-white/80">{t('segments.restaurants.bullet2')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-success-400 flex-shrink-0 mt-0.5" />
                    <span className="text-white/80">{t('segments.restaurants.bullet3')}</span>
                  </li>
                </ul>
                <Button variant="primary">{t('segments.restaurants.cta')}</Button>
              </div>
            </div>
          </GlassPanel>
        </section>

        <section id="segment-multi" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
          <GlassPanel className="p-8">
            <div className="flex items-start gap-4 mb-4">
              <Building2 className="w-10 h-10 text-cyan-400 flex-shrink-0" />
              <div>
                <h2 className="text-2xl font-bold text-white mb-4">{t('segments.multi.title')}</h2>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-success-400 flex-shrink-0 mt-0.5" />
                    <span className="text-white/80">{t('segments.multi.bullet1')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-success-400 flex-shrink-0 mt-0.5" />
                    <span className="text-white/80">{t('segments.multi.bullet2')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-success-400 flex-shrink-0 mt-0.5" />
                    <span className="text-white/80">{t('segments.multi.bullet3')}</span>
                  </li>
                </ul>
                <Button variant="primary">{t('segments.multi.cta')}</Button>
              </div>
            </div>
          </GlassPanel>
        </section>

        <section id="segment-groups" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-24">
          <GlassPanel className="p-8">
            <div className="flex items-start gap-4 mb-4">
              <Hotel className="w-10 h-10 text-violet-400 flex-shrink-0" />
              <div>
                <h2 className="text-2xl font-bold text-white mb-4">{t('segments.groups.title')}</h2>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-success-400 flex-shrink-0 mt-0.5" />
                    <span className="text-white/80">{t('segments.groups.bullet1')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-success-400 flex-shrink-0 mt-0.5" />
                    <span className="text-white/80">{t('segments.groups.bullet2')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-success-400 flex-shrink-0 mt-0.5" />
                    <span className="text-white/80">{t('segments.groups.bullet3')}</span>
                  </li>
                </ul>
                <Button variant="primary">{t('segments.groups.cta')}</Button>
              </div>
            </div>
          </GlassPanel>
        </section>

        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-24">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">{t('copilot.title')}</h2>
            <p className="text-xl text-white/70">{t('copilot.subtitle')}</p>
          </div>

          <GlassPanel className="p-8 max-w-2xl mx-auto">
            <div className="space-y-4">
              {['p1', 'p2', 'p3', 'p4'].map((key) => (
                <div key={key} className="flex items-start gap-3 p-3 rounded-lg bg-white/5 border border-white/10">
                  <Sparkles className="w-5 h-5 text-primary-400 flex-shrink-0 mt-0.5" />
                  <span className="text-white/90">{t(`copilot.${key}`)}</span>
                </div>
              ))}
            </div>
          </GlassPanel>
        </section>

        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mb-24">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">{t('specialists.title')}</h2>
            <p className="text-xl text-white/70 mb-2">{t('specialists.subtitle')}</p>
            <p className="text-sm text-primary-400">{t('specialists.priceFrom')}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { key: 'inventory', icon: Package },
              { key: 'receiving', icon: Camera },
              { key: 'prep', icon: ChefHat },
              { key: 'supplier', icon: Users },
              { key: 'finance', icon: DollarSign },
              { key: 'scheduling', icon: CalendarClock },
            ].map(({ key, icon: Icon }) => (
              <GlassPanel key={key} className="p-6 hover:border-primary-400/50 transition-all">
                <Icon className="w-8 h-8 text-primary-400 mb-3" />
                <h3 className="text-lg font-semibold text-white mb-2">{t(`specialists.${key}.title`)}</h3>
                <p className="text-sm text-white/70">{t(`specialists.${key}.desc`)}</p>
              </GlassPanel>
            ))}
          </div>
        </section>

        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-24">
          <GlassPanel className="p-12 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">{t('future.title')}</h2>
            <p className="text-lg text-white/70 mb-8">{t('future.text')}</p>
            <div className="flex items-center justify-center gap-8">
              <div className="flex flex-col items-center">
                <ShoppingCart className="w-8 h-8 text-white/40 mb-2" />
                <span className="text-sm text-white/50">Retail</span>
              </div>
              <div className="flex flex-col items-center">
                <Stethoscope className="w-8 h-8 text-white/40 mb-2" />
                <span className="text-sm text-white/50">Clinics</span>
              </div>
              <div className="flex flex-col items-center">
                <Dumbbell className="w-8 h-8 text-white/40 mb-2" />
                <span className="text-sm text-white/50">Gyms</span>
              </div>
            </div>
          </GlassPanel>
        </section>

        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-24">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-white mb-8">{t('pricing.title')}</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-4 max-w-2xl mx-auto mb-6">
            <div className="p-4 rounded-lg bg-white/5 border border-white/10">
              <p className="text-white/90">{t('pricing.starter')}</p>
            </div>
            <div className="p-4 rounded-lg bg-white/5 border border-white/10">
              <p className="text-white/90">{t('pricing.pro')}</p>
            </div>
            <div className="p-4 rounded-lg bg-white/5 border border-white/10">
              <p className="text-white/90">{t('pricing.enterprise')}</p>
            </div>
            <div className="p-4 rounded-lg bg-primary-500/20 border border-primary-400/30">
              <p className="text-white/90">{t('pricing.specialists')}</p>
            </div>
          </div>

          <div className="text-center">
            <Link to="/pricing">
              <Button variant="primary" size="lg">
                {t('pricing.cta')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </section>
      </main>

      <footer className="relative border-t border-white/10 bg-black/20 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-5 h-5 text-primary-400" />
              <span className="font-bold text-white">EL-OS</span>
              </div>
              <p className="text-sm text-white/60">{t('footer.tagline')}</p>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-3">{t('footer.company')}</h4>
              <ul className="space-y-2 text-sm text-white/60">
                <li><Link to="/pricing" className="hover:text-white">{t('footer.pricing')}</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-3">{t('footer.product')}</h4>
              <ul className="space-y-2 text-sm text-white/60">
                <li><Link to="/pricing" className="hover:text-white">{t('footer.features')}</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-3">{t('footer.legal')}</h4>
              <ul className="space-y-2 text-sm text-white/60">
                <li><Link to="/privacy" className="hover:text-white">{t('footer.privacy')}</Link></li>
                <li><Link to="/terms" className="hover:text-white">{t('footer.terms')}</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8">
            <p className="text-center text-sm text-white/50">{t('footer.copyright')}</p>
          </div>
        </div>
      </footer>

      <ChatWidget />
    </div>
  );
}

export { LandingPage };
